import { forwardRef } from 'react'
import { Message } from '@/types'
import { MessageBubble } from './MessageBubble'
import { UserSetupForm } from '../forms/UserSetupForm'
import { LogoutModal } from '../modals/LogoutModal'
import { TypingIndicator } from './TypingIndicator'

interface ChatWindowProps {
  // Window state
  isOpen: boolean
  isMinimized: boolean
  unreadMessagesCount: number
  isEmbedded?: boolean

  // User data
  username: string
  email: string
  isSettingUsername: boolean

  // Messages
  messages: Message[]
  newMessage: string
  isLoading: boolean

  // UI state
  error: string | null
  successMessage: string
  isLogoutModalOpen: boolean
  connectionStatus: 'connected' | 'reconnecting' | 'offline'

  // Event handlers
  onClose: () => void
  onMinimize: () => void
  onRestore: () => void
  onLogout: () => void
  onClearHistory?: () => void
  onSendMessage: (_e: React.FormEvent) => void
  onMessageChange: (_e: React.ChangeEvent<HTMLInputElement>) => void
  onSetUsername: (_username: string, _email: string) => void
  onCloseLogoutModal: () => void
  onOpenLogoutModal: () => void
  onTurnstileVerify?: (_token: string) => void
  validateMX?: (_email: string) => Promise<{ valid: boolean; error?: string }>

  // Refs
  messageInputRef: React.RefObject<HTMLInputElement | null>
  messagesEndRef: React.RefObject<HTMLDivElement | null>
}

// Connection status component
const ConnectionStatus = ({ status }: { status: 'connected' | 'reconnecting' | 'offline' }) => {
  if (status === 'connected') {
    return (
      <div className="flex items-center text-xs font-bold text-green-400">
        <div className="mr-1 h-1.5 w-1.5 rounded-full bg-green-400" />
        Live
      </div>
    )
  }

  if (status === 'reconnecting') {
    return (
      <div className="flex items-center text-xs font-bold text-white">
        <div className="mr-1 h-1.5 w-1.5 rounded-full bg-amber-400" />
        Reconnecting
      </div>
    )
  }

  return null
}

// Chat header component
const ChatHeader = ({
  isMinimized,
  unreadMessagesCount,
  connectionStatus,
  isSettingUsername,
  onMinimize,
  onRestore,
  onClose,
  onOpenLogoutModal,
}: {
  isMinimized: boolean
  unreadMessagesCount: number
  connectionStatus: 'connected' | 'reconnecting' | 'offline'
  isSettingUsername: boolean
  onMinimize: () => void
  onRestore: () => void
  onClose: () => void
  onOpenLogoutModal: () => void
}) => (
  <div className="h-[42px] rounded-t-[12px] bg-[#101210] px-4 py-3 text-white transition-colors">
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <span className="mr-2 text-sm font-bold">Studio Graphene</span>
        {isMinimized && unreadMessagesCount > 0 && (
          <div className="bg-chat-notification mr-2 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white">
            {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
          </div>
        )}
        <ConnectionStatus status={connectionStatus} />
      </div>

      <div className="flex justify-between space-x-2">
        <button
          onClick={(e) => {
            e.stopPropagation()
            isMinimized ? onRestore() : onMinimize()
          }}
          className={`cursor-pointer text-xs opacity-75 transition-opacity hover:opacity-100 ${!isMinimized ? 'mt-2' : ''}`}
          title={isMinimized ? 'Expand chat' : 'Minimize chat'}
        >
          <img
            src={isMinimized ? '/images/icons/explandChatIconArrow.svg' : '/images/icons/reduceIcon.svg'}
            alt={isMinimized ? 'expand' : 'minimize'}
            width={16}
            height={16}
            className="h-3.5 w-3.5"
          />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation()
            if (isSettingUsername) {
              // If user hasn't entered name/email yet, close window completely without modal
              onClose()
            } else {
              // Normal behavior: show logout modal when user has already entered credentials
              if (isMinimized) {
                onRestore()
                setTimeout(() => onOpenLogoutModal(), 0)
              } else {
                onOpenLogoutModal()
              }
            }
          }}
          className="cursor-pointer text-xs font-bold opacity-75 transition-opacity hover:opacity-100"
          title="Close chat"
        >
          <img
            src="/images/icons/closeIcon.svg"
            alt="close"
            width={16}
            height={16}
            className="h-3.5 w-3.5"
          />
        </button>
      </div>
    </div>
  </div>
)

// Messages area component
const MessagesArea = ({
  error,
  successMessage,
  messages,
  email,
  messagesEndRef,
  isLoading,
}: {
  error: string | null
  successMessage: string
  messages: Message[]
  email: string
  messagesEndRef: React.RefObject<HTMLDivElement | null>
  isLoading: boolean
}) => (
  <div className="bg-chat-primary relative flex-1 space-y-2 overflow-y-auto p-3">
    {error && (
      <div
        className="relative rounded border border-yellow-200 bg-yellow-50 px-2 py-1 text-xs text-yellow-700"
        role="alert"
      >
        <span className="block">{error}</span>
      </div>
    )}

    {successMessage && (
      <div className="relative rounded border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-800">
        <span className="block">{successMessage}</span>
      </div>
    )}

    {messages.map((message) => (
      <MessageBubble key={message.id} message={message} currentUserEmail={email} />
    ))}

    {isLoading && <TypingIndicator />}

    <div ref={messagesEndRef} />
  </div>
)

// Input section component
const InputSection = ({
  onSendMessage,
  newMessage,
  onMessageChange,
  isLoading,
  messageInputRef,
}: {
  onSendMessage: (_e: React.FormEvent) => void
  newMessage: string
  onMessageChange: (_e: React.ChangeEvent<HTMLInputElement>) => void
  isLoading: boolean
  messageInputRef: React.RefObject<HTMLInputElement | null>
}) => (
  <form onSubmit={onSendMessage} className="bg-chat-primary rounded-b-[12px] p-5 pb-6">
    <div className="flex space-x-2">
      <input
        ref={messageInputRef}
        type="text"
        value={newMessage}
        onChange={onMessageChange}
        placeholder="Ask anything here"
        className="mr-0 flex-1 rounded-full border border-white !bg-transparent px-3 py-2 text-sm !text-white placeholder-white shadow-sm focus:ring-1 focus:outline-none"
        disabled={isLoading}
      />
      <div className="flex items-center space-x-2">
        <button
          onClick={onSendMessage}
          disabled={isLoading || !newMessage.trim()}
          className="relative flex max-h-[40px] w-full cursor-pointer items-center justify-end focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <img src="/images/icons/redArrowRight.svg" alt="send arrow" width={40} height={40} className="rotate-270" />
        </button>
      </div>
    </div>
  </form>
)

export const ChatWindow = forwardRef<HTMLDivElement, ChatWindowProps>((props, ref) => {
  const {
    isOpen,
    isMinimized,
    unreadMessagesCount,
    isEmbedded = false,
    username,
    email,
    isSettingUsername,
    messages,
    newMessage,
    isLoading,
    error,
    successMessage,
    isLogoutModalOpen,
    connectionStatus,
    onClose,
    onMinimize,
    onRestore,
    onLogout,
    onClearHistory,
    onSendMessage,
    onMessageChange,
    onSetUsername,
    onCloseLogoutModal,
    onOpenLogoutModal,
    onTurnstileVerify,
    validateMX,
    messageInputRef,
    messagesEndRef,
  } = props

  if (!isOpen) return null

  return (
    <div
      ref={ref}
      className={`fixed ${isEmbedded ? (isMinimized ? 'top-0 right-0' : 'top-0 right-0') : `right-5 ${isMinimized ? 'bottom-0' : 'bottom-5'}`} z-50 rounded-[12px]`}
    >
      <div className={`flex ${isMinimized ? 'h-auto' : 'h-[485px]'} w-[405px] flex-col rounded-[12px] shadow-2xl`}>
        <ChatHeader
          isMinimized={isMinimized}
          unreadMessagesCount={unreadMessagesCount}
          connectionStatus={connectionStatus}
          isSettingUsername={isSettingUsername}
          onMinimize={onMinimize}
          onRestore={onRestore}
          onClose={onClose}
          onOpenLogoutModal={onOpenLogoutModal}
        />

        {/* Chat Content */}
        {!isMinimized && (
          <>
            <LogoutModal isOpen={isLogoutModalOpen} onClose={onCloseLogoutModal} onConfirm={onLogout} />

            {isSettingUsername ? (
              <UserSetupForm
                initialUsername={username}
                initialEmail={email}
                onSubmit={onSetUsername}
                onTurnstileVerify={onTurnstileVerify}
                validateMX={validateMX}
              />
            ) : (
              <>
                <MessagesArea
                  error={error}
                  successMessage={successMessage}
                  messages={messages}
                  email={email}
                  messagesEndRef={messagesEndRef}
                  isLoading={isLoading}
                />

                {/* Clear History Button - only show if messages exist */}
                {messages.length > 0 && onClearHistory && (
                  <div className="px-4 pb-2">
                    <button
                      onClick={onClearHistory}
                      className="text-xs text-gray-400 hover:text-gray-200 underline transition-colors"
                      title="Clear all chat history"
                    >
                      Clear History
                    </button>
                  </div>
                )}

                <InputSection
                  onSendMessage={onSendMessage}
                  newMessage={newMessage}
                  onMessageChange={onMessageChange}
                  isLoading={isLoading}
                  messageInputRef={messageInputRef}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
})

ChatWindow.displayName = 'ChatWindow'
